import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import Close16 from '@carbon/icons-react/es/close/16';
import Menu16 from '@carbon/icons-react/es/menu/16';
import Save16 from '@carbon/icons-react/es/save/16';
import { Dropdown, Tooltip } from 'antd';
import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { View } from 'api/views/types';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown/menu';
import { confirm } from 'components/atoms/modal';
import { SingleLineEllipsis } from 'components/styles';
import { PROJECT_LIST_VIEW_QUERY_KEY, RoutePaths } from 'constant';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useIsExternalUser } from 'hooks/use-user-type';
import { Filter } from '../filters/types';
import { toastService } from '../toast';
import { ButtonWrapper, HoverableLinkButton } from './components';
import { ManageProjectViewDrawer } from './manage-project-view-drawer';
import { ManageViewDrawer } from './manage-view-drawer';
import { ManageViewModal } from './manage-view-modal';
import { SavedViewsSideBar } from './saved-views';
import { ViewContextProps, ViewContextProvider } from './saved-views/context';
import { CreatableView, Views } from './types';

const EllipsisWrapper = styled.span`
  ${SingleLineEllipsis};
`;

const MenuItemStyled = styled(Menu.Item)<{ cursorType: boolean }>`
  cursor: ${({ cursorType }) => (cursorType ? 'not-allowed' : 'pointer')};
`;

const PaddedHoverableLinkButton = styled(HoverableLinkButton)`
  padding-left: var(--base-unit);
  padding-right: var(--base-unit);
`;

const ClearButton = styled(Button)`
  margin-right: var(--spacing-03);
`;

interface Props<ViewDefinition = any> {
  contextValue: ViewContextProps;
  views: Views<ViewDefinition>;
  defaultViewsList: Views<ViewDefinition>;
  defaultViewId: number;
  filterComponent: ReactNode;
  additionalComponent?: (componentProps) => ReactNode;
  additionComponentProps?: any;
  onViewSelect: (view: View<ViewDefinition>, isClearAll?: boolean) => void;
  onViewUpdate: (view: View<ViewDefinition>) => Promise<View<ViewDefinition>>;
  onViewCreate: (
    view: CreatableView<ViewDefinition>,
  ) => Promise<View<ViewDefinition>>;
  onViewDelete: (view: View<ViewDefinition>) => Promise<View<ViewDefinition>>;
  onProjectViewCreate?: (
    view: CreatableView<ViewDefinition>,
  ) => Promise<View<ViewDefinition>>;
  onProjectViewUpdate?: (
    view: View<ViewDefinition>,
  ) => Promise<View<ViewDefinition>>;
  isValidViewDefinition: (definition: ViewDefinition) => boolean;
  viewDefinitionComparator: (
    oldDefinition: ViewDefinition,
    newDefinition: ViewDefinition,
    isProjectTaskList: boolean,
    isExternAlUser: boolean,
  ) => boolean;
  currentViewDefinition: ViewDefinition;
  selectedViewId: number;
  includeSavedViews?: boolean;
  defaultSharedFilter?: Filter;
  isDashboardView?: boolean;
  showClearBtn?: boolean;
  canSaveViews?: boolean;
  showSaveViewBtn?: boolean;
}

export const SavedViews: FC<Props> = ({
  contextValue,
  defaultViewsList,
  defaultViewId,
  views,
  filterComponent,
  additionalComponent,
  additionComponentProps = {},
  onViewSelect,
  currentViewDefinition,
  isValidViewDefinition,
  viewDefinitionComparator,
  onViewUpdate,
  onViewCreate,
  onViewDelete,
  onProjectViewCreate,
  onProjectViewUpdate,
  selectedViewId,
  includeSavedViews = true,
  defaultSharedFilter,
  isDashboardView = false,
  showClearBtn = true,
  canSaveViews = true,
}) => {
  const {
    user: { userId: currentUserId },
  } = useUser();
  const translate = useTranslate();
  const [manageViewModalVisible, setManageViewModalVisible] = useState(false);
  const [savedViewsVisible, setSavedViewsVisibility] = useState(false);
  const selectedViewWithState = useMemo(
    () =>
      views.byIds[selectedViewId] ??
      defaultViewsList.byIds[selectedViewId] ??
      defaultViewsList.byIds[defaultViewId],
    [defaultViewId, selectedViewId, views.byIds, defaultViewsList.byIds],
  );

  const { pathname } = useLocation();
  const isTaskPage = pathname.includes('/tasks');
  const isProjectTaskList = pathname.includes('/projects') && isTaskPage;
  const isExternalUser = useIsExternalUser();
  const queryClient = useQueryClient();

  const selectedView = useMemo(() => {
    if (!selectedViewWithState) {
      return undefined;
    }

    return selectedViewWithState[0] === 'updating'
      ? selectedViewWithState[2]
      : selectedViewWithState[1];
  }, [selectedViewWithState]);

  const isCurrentDefinitionValid = useMemo(
    () => isValidViewDefinition(currentViewDefinition),
    [isValidViewDefinition, currentViewDefinition],
  );
  const [currentEditingView, setCurrentEditingView] = useState<View<any>>();

  const isMySavedViewSelected = useMemo(
    () => selectedView?.createdBy === currentUserId,
    [selectedView, currentUserId],
  );

  const isViewDefinitionChanged = useMemo(() => {
    if (!selectedView) {
      return false;
    }

    return viewDefinitionComparator(
      selectedView.viewDefinition,
      currentViewDefinition,
      true,
      isExternalUser,
    );
  }, [
    currentViewDefinition,
    isExternalUser,
    selectedView,
    viewDefinitionComparator,
  ]);

  const selectedViewName = useMemo(() => {
    if (selectedView) {
      if (
        isExternalUser &&
        selectedView.viewName === translate('all_tasks.filters.all_tasks')
      ) {
        return translate('all_tasks');
      }

      return selectedView.viewName;
    }

    return '';
  }, [isExternalUser, selectedView, translate]);

  const onViewEdit = (view) => {
    setCurrentEditingView(view);
    setManageViewModalVisible(true);
  };

  const updateMenuItemLabel = useMemo(
    () =>
      translate('views.buttons.update_existing_view', {
        viewName: selectedView?.viewName,
        strong: (chunks) => <strong>&nbsp;{chunks}</strong>,
      }),
    [selectedView?.viewName, translate],
  );

  const disableUpdateForViewFromAllTasks = useMemo(() => {
    if (views.byIds[selectedViewId]) {
      return (
        views.byIds[selectedViewId][1].viewFilterShareConfig
          ?.shareWithProjects && isProjectTaskList
      );
    }

    return false;
  }, [isProjectTaskList, selectedViewId, views.byIds]);

  const onUpdateSuccess = useCallback(
    (view) => {
      toastService({
        title: translate('success'),
        message: translate('views.update.success', {
          viewName: view.viewName,
        }),
      }).success();
    },
    [translate],
  );

  const onUpdateFailure = useCallback(
    (view) => {
      toastService({
        title: translate('failure'),
        message: translate('views.update.failed', {
          viewName: view.viewName,
        }),
      }).error();
    },
    [translate],
  );

  const onUpdate = useCallback(
    (view: View<any>) => {
      confirm({
        okText: translate('update'),
        okType: 'primary',
        cancelText: translate('cancel'),
        onCancel: () => {},
        title: translate('views.update_confirmation.title'),
        content: (
          <>
            {translate('views.also.available.in', {
              viewName: view.viewName,
              strong: (chunks) => <strong>{chunks}</strong>,
            })}
            <ul>
              {view.viewFilterShareConfig?.sharedWithCustomerPortal
                ? translate('views.found.in', {
                    li: (chunks) => <li>{chunks}</li>,
                  })
                : translate('views.found.in.only.project.list', {
                    li: (chunks) => <li>{chunks}</li>,
                  })}
            </ul>
            <div>{translate('views.update.from.everywhere')}&nbsp;</div>
            <div>{translate('views.update_confirmation.text')}</div>
          </>
        ),
        onOk: () => {
          onViewUpdate({
            ...view,
            viewDefinition: currentViewDefinition,
          })
            .then(() => onUpdateSuccess(view))
            .catch(() => onUpdateFailure(view));
        },

        bodyStyle: { width: '500px' },
      });
    },
    [
      translate,
      onViewUpdate,
      currentViewDefinition,
      onUpdateSuccess,
      onUpdateFailure,
    ],
  );

  const updateSelectedView = useCallback(() => {
    if (!selectedView) {
      return;
    }

    if (isProjectTaskList) {
      onProjectViewUpdate?.({
        ...selectedView,
        viewDefinition: currentViewDefinition,
      })
        .then(() => onUpdateSuccess(selectedView))
        .catch(() => onUpdateFailure(selectedView));

      return;
    }

    if (
      selectedView.self ||
      !selectedView.viewFilterShareConfig?.shareWithProjects
    ) {
      onViewUpdate({
        ...selectedView,
        viewDefinition: currentViewDefinition,
      })
        .then(() => {
          onUpdateSuccess(selectedView);
          queryClient.invalidateQueries(PROJECT_LIST_VIEW_QUERY_KEY);
        })
        .catch(() => onUpdateFailure(selectedView));

      return;
    }

    onUpdate(selectedView);
  }, [
    selectedView,
    isProjectTaskList,
    onUpdate,
    onProjectViewUpdate,
    currentViewDefinition,
    onUpdateSuccess,
    onUpdateFailure,
    onViewUpdate,
    queryClient,
  ]);

  const onCreate = useCallback(
    (view: CreatableView<any>) => {
      return onViewCreate(view).then((v) => {
        onViewSelect(v);

        return v;
      });
    },
    [onViewCreate, onViewSelect],
  );

  const onCreateProject = useCallback(
    (view: CreatableView<any>) => {
      return onProjectViewCreate!(view).then((v) => {
        onViewSelect(v);

        return v;
      });
    },
    [onProjectViewCreate, onViewSelect],
  );

  // TODO this is duplicated in filter-menu as well. Need to come up with a common handling
  const onDelete = useCallback(
    (view: View<any>) => {
      confirm({
        okText: translate('delete'),
        okType: 'danger',
        cancelText: translate('cancel'),
        onCancel: () => {},
        title: translate('views.delete_confirmation.title'),
        content: translate('views.delete_confirmation.content', {
          viewName: view.viewName,
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        onOk: () => {
          if (view.viewId === selectedViewId) {
            onViewSelect(defaultViewsList.byIds[defaultViewId][1]);
          }
          onViewDelete(view)
            .then(() => {
              toastService({
                title: translate('success'),
                message: translate('views.delete.success', {
                  viewName: view.viewName,
                }),
              }).success();
              queryClient.invalidateQueries(PROJECT_LIST_VIEW_QUERY_KEY);
            })
            .catch(() => {
              toastService({
                title: translate('failure'),
                message: translate('views.delete.failed', {
                  viewName: view.viewName,
                }),
              }).error();
            });
        },
      });
    },
    [
      translate,
      selectedViewId,
      onViewDelete,
      onViewSelect,
      defaultViewsList.byIds,
      defaultViewId,
      queryClient,
    ],
  );

  const onCloseViewDrawer = useCallback(() => {
    setManageViewModalVisible(false);
    setCurrentEditingView(undefined);
  }, []);

  const onViewSelectFromSidebar = useCallback(
    (view) => {
      onViewSelect(view);
      setSavedViewsVisibility(false);
    },
    [onViewSelect],
  );

  const isMyTaskView = window.location.href.includes(RoutePaths.myTasks);
  const viewPlaceHolder = isDashboardView ? 'filter' : 'view';

  const SaveViews = () => {
    return (
      <>
        {isCurrentDefinitionValid &&
        isViewDefinitionChanged &&
        !isExternalUser &&
        views.ids[0] !== 'loading' &&
        !isMyTaskView &&
        canSaveViews ? (
          <>
            {!isMySavedViewSelected ? (
              <Tooltip
                placement="topLeft"
                title={translate(`views.buttons.save_${viewPlaceHolder}_as`)}
              >
                <HoverableLinkButton
                  size="small"
                  type="link"
                  icon={<Save16 />}
                  onClick={() => setManageViewModalVisible(true)}
                  data-cy="buttons.save-view-as"
                />
              </Tooltip>
            ) : isViewDefinitionChanged ? (
              <Dropdown
                overlay={
                  <Menu data-cy="views.edit-options">
                    <MenuItemStyled
                      onClick={() =>
                        !disableUpdateForViewFromAllTasks &&
                        updateSelectedView()
                      }
                      cursorType={disableUpdateForViewFromAllTasks ?? false}
                    >
                      <Tooltip
                        title={
                          disableUpdateForViewFromAllTasks ? (
                            <FormattedMessage
                              id="views.update.from.all.tasks"
                              values={{
                                b: (chunks) => <strong>{chunks}</strong>,
                              }}
                            />
                          ) : (
                            updateMenuItemLabel
                          )
                        }
                      >
                        <EllipsisWrapper>{updateMenuItemLabel}</EllipsisWrapper>
                      </Tooltip>
                    </MenuItemStyled>
                    <Menu.Item onClick={() => setManageViewModalVisible(true)}>
                      <FormattedMessage
                        id={`views.buttons.save_${viewPlaceHolder}_as_new`}
                      />
                    </Menu.Item>
                  </Menu>
                }
                placement="bottomLeft"
                trigger={['hover']}
              >
                <PaddedHoverableLinkButton
                  data-cy="save.view.button"
                  size="small"
                  type="link"
                  icon={<ChevronDown16 />}
                  iconPlacement="right"
                  loading={
                    selectedViewWithState[0] === 'updating' ||
                    selectedViewWithState[0] === 'creating'
                  }
                >
                  <Save16 />
                </PaddedHoverableLinkButton>
              </Dropdown>
            ) : null}
          </>
        ) : null}
      </>
    );
  };

  return (
    <ViewContextProvider value={contextValue}>
      {includeSavedViews ? (
        <ButtonWrapper>
          <Tooltip
            title={
              selectedView ? (
                selectedView.viewName
              ) : (
                <FormattedMessage id="views.saved_view.title" />
              )
            }
          >
            <HoverableLinkButton
              data-cy="buttons.views-sider-open"
              icon={<Menu16 />}
              onClick={() => setSavedViewsVisibility(true)}
            >
              {selectedViewName}
            </HoverableLinkButton>
          </Tooltip>
          {filterComponent}
          <SaveViews />
        </ButtonWrapper>
      ) : (
        <ButtonWrapper>
          {additionalComponent
            ? additionalComponent({
                ...additionComponentProps,
                onViewDelete,
                selectedViewId,
                onViewEdit,
              })
            : null}
          {filterComponent}
          <SaveViews />
        </ButtonWrapper>
      )}
      {isViewDefinitionChanged && showClearBtn ? (
        <Tooltip title={<FormattedMessage id="filter.reset.tooltip" />}>
          <ClearButton
            size="small"
            type="text"
            data-cy="buttons.clear-view"
            icon={<Close16 color="var(--primary-color)" />}
            onClick={() => {
              if (selectedView) {
                onViewSelect(selectedView, true);
              }
            }}
          />
        </Tooltip>
      ) : null}
      {(isTaskPage || isDashboardView) && !isProjectTaskList ? (
        <ManageViewDrawer
          visible={manageViewModalVisible}
          onClose={onCloseViewDrawer}
          view={currentEditingView}
          onViewCreate={onCreate}
          onViewUpdate={onViewUpdate}
          currentViewDefinition={currentViewDefinition}
          defaultSharedFilter={defaultSharedFilter}
          isDashboardView={isDashboardView}
        />
      ) : null}
      {isTaskPage && isProjectTaskList ? (
        <ManageProjectViewDrawer
          visible={manageViewModalVisible}
          onClose={onCloseViewDrawer}
          view={currentEditingView}
          onViewCreate={onCreateProject}
          onViewUpdate={onProjectViewUpdate}
          currentViewDefinition={currentViewDefinition}
        />
      ) : null}
      {!isTaskPage && !isDashboardView ? (
        <ManageViewModal
          visible={manageViewModalVisible}
          onClose={onCloseViewDrawer}
          view={currentEditingView}
          onViewCreate={onCreate}
          onViewUpdate={onViewUpdate}
          currentViewDefinition={currentViewDefinition}
        />
      ) : null}
      {includeSavedViews ? (
        <SavedViewsSideBar
          contextValue={contextValue}
          visible={savedViewsVisible}
          onClose={() => setSavedViewsVisibility(false)}
          views={views}
          defaultViewsList={defaultViewsList}
          defaultViewId={defaultViewId}
          onViewSelect={(view) => onViewSelectFromSidebar(view)}
          selectedView={selectedView}
          onViewEdit={onViewEdit}
          onViewDelete={onDelete}
          loading={views.ids[0] === 'loading'}
        />
      ) : (
        ''
      )}
    </ViewContextProvider>
  );
};
